import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';

export default function Login() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot Password flow states
  const [forgotView, setForgotView] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: Send OTP, 2: Verify OTP, 3: Reset Password
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.username, form.password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(user.role === 'admin' ? '/admin/dashboard' : '/billing');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequestOtp = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const res = await API.post('/auth/forgot-password', { username: 'admin' });
      toast.success(res.data.message || 'OTP sent successfully!');
      setForgotStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) return toast.error('OTP is required');
    setForgotLoading(true);
    try {
      const res = await API.post('/auth/verify-otp', { otp });
      toast.success(res.data.message || 'OTP verified successfully!');
      setForgotStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) return toast.error('All fields are required');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');

    setForgotLoading(true);
    try {
      const res = await API.post('/auth/reset-password', { password: newPassword });
      toast.success(res.data.message || 'Password reset successful!');
      setForgotView(false);
      setForgotStep(1);
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setForm({ username: 'admin', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-left">
        <div style={{ maxWidth: 400 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <img src="/logo.jpg" alt="Logo" style={{ width: '48px', height: '48px', borderRadius: '8px', border: '2px solid var(--gold)', objectFit: 'cover' }} />
            <div className="login-logo" style={{ marginBottom: 0 }}>New<span>Sowtha</span></div>
          </div>
          <p className="login-tagline">Professional Store Billing & Management</p>
          <div style={{ marginTop: 48 }}>
            {[
              { icon: '🧾', title: 'Smart Billing', desc: 'Generate professional invoices instantly' },
              { icon: '📊', title: 'EMI Tracking', desc: 'Track customer payments & installments' },
              { icon: '📦', title: 'Inventory', desc: 'Real-time stock management' },
            ].map(f => (
              <div key={f.title} className="login-feature">
                <div className="login-feature-icon">{f.icon}</div>
                <div><h4>{f.title}</h4><p>{f.desc}</p></div>
              </div>
            ))}
          </div>

        </div>
      </div>
      <div className="login-right">
        <div className="login-card">
          {forgotView ? (
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--navy)', marginBottom: 6 }}>Admin Reset</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: 24 }}>Forgot password recovery for newsowthaoffice@gmail.com</p>

              {forgotStep === 1 && (
                <form onSubmit={handleForgotRequestOtp}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 18 }}>
                    We will send a 6-digit verification code to the registered email address.
                  </p>
                  <button type="submit" className="btn btn-gold btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={forgotLoading}>
                    {forgotLoading ? '⏳ Sending OTP...' : '📨 Send OTP'}
                  </button>
                </form>
              )}

              {forgotStep === 2 && (
                <form onSubmit={handleForgotVerifyOtp}>
                  <div className="form-group">
                    <label className="form-label">Verification Code (OTP)</label>
                    <input className="form-control" placeholder="Enter 6-digit OTP" value={otp}
                      onChange={e => setOtp(e.target.value)} required />
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 8, justifyContent: 'center' }} disabled={forgotLoading}>
                    {forgotLoading ? '⏳ Verifying...' : '✓ Verify OTP'}
                  </button>
                </form>
              )}

              {forgotStep === 3 && (
                <form onSubmit={handleForgotResetPassword}>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input className="form-control" type="password" placeholder="Enter new password" value={newPassword}
                      onChange={e => setNewPassword(e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <input className="form-control" type="password" placeholder="Confirm new password" value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)} required />
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 8, justifyContent: 'center' }} disabled={forgotLoading}>
                    {forgotLoading ? '⏳ Resetting...' : '🔑 Reset Password'}
                  </button>
                </form>
              )}

              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <button className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setForgotView(false); setForgotStep(1); }} disabled={forgotLoading}>
                  🚪 Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--navy)', marginBottom: 6 }}>Welcome back</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: 32 }}>Sign in to access your portal</p>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input className="form-control" placeholder="Enter your username" value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value })} required />
                </div>
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="form-label" style={{ marginBottom: 0 }}>Password</label>
                    {form.username === 'admin' && (
                      <button type="button" style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: '0.78rem', cursor: 'pointer', padding: 0 }} onClick={() => setForgotView(true)}>
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <input className="form-control" type="password" placeholder="Enter your password" value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })} required style={{ marginTop: 6 }} />
                </div>
                <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 8, justifyContent: 'center' }} disabled={loading}>
                  {loading ? '⏳ Signing in...' : '🔐 Sign In'}
                </button>
              </form>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
